import { useEffect, useRef } from 'react';

import { DATADOG_ACTIONS } from '../../../constants';
import { useDataDogActions } from '../../../hooks/useDataDogActions';
import { useVisibilityCheck } from '../../../hooks/useVisibilityCheck';

type VoWifiStep = 'splash' | 'terms' | 'address' | 'confirmation';

/**
 * Render-quality DataDog tracking shared by the VoWifi screens.
 *
 * - `containerRef`: attach to the screen's root element. Runs useVisibilityCheck
 *   (flow: 'render') to catch blank / invisible / broken-branding renders.
 *   `checkPresence` is off because each screen mounts on its own wizard step —
 *   a null ref on inactive steps must stay a no-op, not a false MISSING_DOM.
 * - `onImageError`: pass to HeaderImage's `onError` to report logo load
 *   failures (flow: 'render_logo').
 *
 * - `step`: emits a `step_viewed` event on mount so DataDog can chart the
 *   funnel / drop-off across the wizard steps (StepWizard unmounts inactive
 *   steps, so a mount = the step became visible).
 *
 * All emissions are gated to non-preview to avoid polluting production metrics.
 */
export const useVoWifiRenderTracking = (
  isPortalPreview: boolean,
  step: VoWifiStep,
) => {
  const { dataDogLogger, actions } = useDataDogActions({
    isPortalPreview,
    actionKey: 'vowifi',
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isPortalPreview) {
      dataDogLogger.log(actions?.step_viewed, { step });
    }
    // Fire once per mount; StepWizard remounts on step change.
  }, []);

  useVisibilityCheck(containerRef, {
    enabled: !isPortalPreview,
    checkPresence: false,
    logger: dataDogLogger,
    errorAction: DATADOG_ACTIONS.errors.template_error,
    flow: 'render',
  });

  const onImageError = (imageUrl: string) => {
    if (isPortalPreview) {
      return;
    }

    dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
      flow: 'render_logo',
      statusCode: 0,
      errorCode: 'IMAGE_LOAD_FAILED',
      errorMessage: `Logo image failed to load: ${imageUrl}`,
      imageUrl,
    });
  };

  return { containerRef, onImageError };
};
